import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX, ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EOnboardingTaskState } from '@feathr/blackbox';
import { Button, CardV2 as Card, DetailedProgressBar } from '@feathr/components';
import { useOnboardingStatus } from '@feathr/extender/hooks';
import { useFlags } from '@feathr/report_components';

import type { IMilestoneConfig } from '../Milestones.types';

import * as styles from './Milestone.css';

interface IMilestoneProps extends Omit<IMilestoneConfig, 'actionUrl'> {
  onClick?: () => void;
  remainingTime: number;
  state: EOnboardingTaskState;
  totalTime: number;
  additionalContent?: ReactNode;
}

function Milestone({
  title,
  description,
  onClick,
  remainingTime,
  state = EOnboardingTaskState.NotStarted,
  totalTime,
  progressiveFeature,
  additionalContent,
}: Readonly<IMilestoneProps>): JSX.Element {
  const { t } = useTranslation();
  const { enforceOnboarding } = useFlags();
  const [isDisabled, setIsDisabled] = useState(false);
  const {
    isLoading,
    milestone: prerequisiteMilestone,
    message: prerequisiteMessage,
  } = useOnboardingStatus(progressiveFeature, true);

  useEffect(() => {
    if (!progressiveFeature) {
      return;
    }
    setIsDisabled(false);
    if (enforceOnboarding && prerequisiteMilestone?.state !== EOnboardingTaskState.Completed) {
      setIsDisabled(true);
    }
  }, [prerequisiteMilestone]);

  const buttonTooltip = enforceOnboarding ? prerequisiteMessage : undefined;

  function formatTime(value: number): JSX.Element {
    return value > 0 ? (
      <span>~{t('{{count}} minute', { count: value })}</span>
    ) : (
      <span>{t('{{count}} minute', { count: value })}</span>
    );
  }

  const titleElement = (
    <h3 className={styles.title}>
      {state === EOnboardingTaskState.Completed && (
        <FontAwesomeIcon className={styles.checkIcon} icon={faCircleCheck} />
      )}
      {title}
    </h3>
  );
  const descriptionElement = <span className={styles.description}>{description}</span>;

  const actionLabels = {
    [EOnboardingTaskState.Completed]: t('View'),
    [EOnboardingTaskState.InProgress]: t('Continue'),
    [EOnboardingTaskState.NotStarted]: t('Start'),
  };

  return (
    <Card width={'full'}>
      <Card.Content
        action={
          <Button
            disabled={isDisabled}
            isLoading={isLoading}
            onClick={onClick}
            tooltip={buttonTooltip}
            type={state === 'completed' ? 'success' : 'primary'}
          >
            {actionLabels[state]}
          </Button>
        }
        description={descriptionElement}
        headerAlign={'top'}
        padding={'relaxed'}
        title={titleElement}
      >
        {additionalContent}
        <DetailedProgressBar.Milestone
          className={styles.progressBar}
          endLabel={t('total')}
          endValue={totalTime}
          progressMode={'remaining'}
          shade={600}
          startLabel={t('remaining')}
          startValue={remainingTime}
          theme={'green'}
          valueFormatter={formatTime}
        />
      </Card.Content>
    </Card>
  );
}

export default Milestone;
